import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { resolveArea } from '../constants/ticketOptions';
import toast from 'react-hot-toast';
import NewTicketView from './NewTicket.view';

// Stable entity IDs (DB constants) — avoids a /entities round-trip.
const ENTITY_BUTTONS = [
  { id: '1', slug: 'abraaj-water',    label: 'Abraaj Water' },
  { id: '2', slug: 'lofat-lifestyle', label: 'Lofat Lifestyle' },
  { id: '3', slug: 'regency-kuwait',  label: 'Regency Kuwait' },
];

const PHOTO_MAX = 5;
const PHOTO_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

export default function NewTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [complaintData, setComplaintData] = useState({});
  const [uiGroup, setUiGroup] = useState('');
  const [agents, setAgents] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);

  const [form, setForm] = useState({
    // super_admin defaults to Abraaj Water (entity id 1); others use their own entity
    entity_id: user?.role === 'super_admin' ? '1' : (String(user?.entity_id || '') || ''),
    subject: '',
    complaint_group: '',
    complaint_type: '',
    complaint_subtype: '',
    complaint_description: '',
    channel_source: '',
    customer_mobile: '',
    customer_name: '',
    customer_segment: '',
    customer_area: '',
    customer_email: '',
    sap_order_reference: '',
    platform_order_reference: '',
    order_source: '',
    product_type: '',
    product_sku: '',
    product_quantity: '',
    product_quantity_unit: '',
    product_sku_brand: '',
    sap_route: '',
    cancellation_reason: '',
    icarry_reference: '',
    replacement_order_reference: '',
    meal_date: '',
    assigned_to: '',
    department_assigned: '',
    escalation_reason: '',
    hubspot_contact_id: '',
    complaint_priority: '',
  });
  const [photos, setPhotos] = useState([]);
  const [autoPriority, setAutoPriority] = useState('');
  const [errors, setErrors] = useState({});
  const [areaSearch, setAreaSearch] = useState('');
  const [lastLookedUp, setLastLookedUp] = useState('');

  const [customerLookup, setCustomerLookup] = useState({ status: 'idle' });
  const [ordersState, setOrdersState] = useState({ status: 'idle', orders: [], selectedDealId: null });
  const [orderMeta, setOrderMeta] = useState({ custcat1: '', custcat2: '' });
  const [ticketHistory, setTicketHistory] = useState({ status: 'idle', tickets: [] });

  const ticketHistoryIdRef = useRef(0);
  const hubspotContactIdRef = useRef('');
  const fetchOrdersIdRef = useRef(0);
  const entitySetByContactRef = useRef(false);

  useEffect(() => {
    api.get('/complaint-data').then((r) => setComplaintData(r.data)).catch(() => {});
  }, []);

  const getEntitySlug = () => {
    if (user?.role !== 'super_admin') return user?.entity_slug || '';
    return ENTITY_BUTTONS.find((b) => b.id === String(form.entity_id))?.slug || '';
  };

  const entitySlug = getEntitySlug();
  const entitySlugRef = useRef(entitySlug);
  entitySlugRef.current = entitySlug;

  // Live routing rules for the auto-route hint
  useEffect(() => {
    if (!entitySlug) { setRoutingRules([]); return; }
    api.get(`/routing-rules?entity_slug=${entitySlug}`)
      .then((r) => setRoutingRules(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRoutingRules([]));
  }, [entitySlug]);

  // Agents (for Assignment card)
  useEffect(() => {
    if (form.entity_id) {
      api.get('/users', { params: { entity_id: form.entity_id } })
        .then((r) => setAgents(r.data.filter((u) => u.role === 'agent' || u.role === 'entity_manager')))
        .catch(() => setAgents([]));
    }
  }, [form.entity_id]);

  // Clear stale customer/orders/history when entity changes
  const prevEntitySlugRef = useRef(entitySlug);
  useEffect(() => {
    if (prevEntitySlugRef.current !== entitySlug) {
      prevEntitySlugRef.current = entitySlug;
      fetchOrdersIdRef.current += 1;
      ticketHistoryIdRef.current += 1;
      if (entitySetByContactRef.current) {
        entitySetByContactRef.current = false;
        setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
        setOrderMeta({ custcat1: '', custcat2: '' });
        setTicketHistory({ status: 'idle', tickets: [] });
        return;
      }
      setCustomerLookup({ status: 'idle' });
      setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
      setOrderMeta({ custcat1: '', custcat2: '' });
      setTicketHistory({ status: 'idle', tickets: [] });
      hubspotContactIdRef.current = '';
      setForm((prev) => ({ ...prev, customer_name: '', customer_email: '', sap_order_reference: '', hubspot_contact_id: '', department_assigned: '', complaint_group: '', complaint_type: '', complaint_subtype: '' }));
      setUiGroup('');
    }
  }, [entitySlug]);

  const groups = (entitySlug && complaintData[entitySlug]) ? Object.keys(complaintData[entitySlug].groups) : [];
  const types = (entitySlug && uiGroup && complaintData[entitySlug]?.groups[uiGroup]) || [];
  const isLofat = entitySlug?.includes('lofat');
  const isAbraaj = entitySlug?.includes('abraaj');
  const entityLocked = user?.role !== 'super_admin';

  // Live routing hint: type → group slug → active rule → department
  const routingHint = (() => {
    if (!form.complaint_type) return null;
    const slug = complaintData[entitySlug]?.typeToSlug?.[form.complaint_type];
    const rule = slug && routingRules.find((r) => r.complaint_group === slug && r.is_active && !r.is_fallback);
    return rule?.department || null;
  })();

  const computePriority = (type) => {
    const CRITICAL = ['Contamination', 'Food Poisoning', 'Allergic Reaction', 'Foreign Body', 'Foreign Object', 'Wrong Driver', 'Driver Misconduct', 'Delayed Refund', 'Wrong Dietary Requirements', 'Refund Status', 'Payment Discrepancy'];
    const HIGH = ['Delay Order', 'Non-delivery', 'Non-Delivery', 'Damaged Product', 'Wrong Product', 'Refund Request', 'Driver Behavior', 'Overcharged', 'Modify', 'Missing Items', 'Wrong Box', 'Late Delivery', 'Wrong Diet Plan', 'Wrong Ingredients', 'Temperature Issue', 'Partial Delivery', 'Taste', 'Smell', 'Colour / Appearance', 'Packaging Damage', 'Expired or Wrong Product', 'Wrong Address', 'Cancel-Delay', 'Cancel-Other', 'Exchange'];
    const MEDIUM = ['Cancel', 'Exchange', 'KNET', 'App Issue', 'Merak', 'Incorrect Portion', 'Payment Failure', 'Payment Link', 'Price Inquiry'];
    let p = '';
    if (CRITICAL.some((t) => type?.includes(t))) p = 'critical';
    else if (HIGH.some((t) => type?.includes(t))) p = 'high';
    else if (MEDIUM.some((t) => type?.includes(t))) p = 'medium';
    else if (type) p = 'low';
    setAutoPriority(p);
    setForm((prev) => ({ ...prev, complaint_priority: p }));
  };

  const set = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'complaint_group') { next.complaint_type = ''; next.complaint_subtype = ''; }
      if (key === 'complaint_type') {
        next.complaint_subtype = '';
        computePriority(value);
        const typeToSlug = complaintData[entitySlug]?.typeToSlug;
        if (typeToSlug && value) next.complaint_group = typeToSlug[value] || 'general_inquiry';
      }
      return next;
    });
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const fetchOrders = async (contactId) => {
    if (!contactId) return;
    const slug = entitySlugRef.current || '';
    fetchOrdersIdRef.current += 1;
    const myFetchId = fetchOrdersIdRef.current;
    setOrdersState({ status: 'loading', orders: [], selectedDealId: null });
    setOrderMeta({ custcat1: '', custcat2: '' });
    try {
      const url = `/hubspot/orders?contactId=${contactId}&entity=${encodeURIComponent(slug)}`;
      const res = await api.get(url);
      if (myFetchId !== fetchOrdersIdRef.current) return;
      setOrdersState({ status: 'done', orders: res.data.orders || [], selectedDealId: null });
    } catch {
      if (myFetchId !== fetchOrdersIdRef.current) return;
      setOrdersState({ status: 'done', orders: [], selectedDealId: null });
    }
  };

  // Ticket history keyed on mobile so duplicate HubSpot contacts still aggregate.
  const fetchTicketHistory = async (mobile) => {
    const raw = (mobile || '').trim();
    if (!raw) return;
    const clean = raw.replace(/^\+?965/, '').replace(/\+/, '').trim();
    if (!clean || clean.length < 4) return;
    ticketHistoryIdRef.current += 1;
    const myId = ticketHistoryIdRef.current;
    setTicketHistory({ status: 'loading', tickets: [] });
    try {
      const slug = entitySlugRef.current || '';
      const url = `/hubspot/contact-tickets?mobile=${encodeURIComponent(clean)}${slug ? '&entity=' + encodeURIComponent(slug) : ''}`;
      const res = await api.get(url);
      if (myId !== ticketHistoryIdRef.current) return;
      setTicketHistory({ status: 'done', tickets: res.data.tickets || [] });
    } catch {
      if (myId !== ticketHistoryIdRef.current) return;
      setTicketHistory({ status: 'done', tickets: [] });
    }
  };

  const handleSelectCustomer = (index) => {
    const customers = customerLookup.customers;
    if (!customers || index >= customers.length) return;
    const c = customers[index];
    setCustomerLookup((prev) => ({ ...prev, selectedIndex: index }));

    const emailToUse = !entitySlug
      ? (c.email || customers.find((x) => x.email)?.email || '')
      : (c.email || '');

    const formUpdates = { customer_name: c.name || '', customer_email: emailToUse, hubspot_contact_id: c.contactId || '' };

    if (!entitySlug && c.entitySlug) {
      const matched = ENTITY_BUTTONS.find((b) => b.slug === c.entitySlug);
      if (matched) {
        formUpdates.entity_id = matched.id;
        entitySlugRef.current = c.entitySlug;
        entitySetByContactRef.current = true;
      }
    }

    hubspotContactIdRef.current = c.contactId || '';
    setForm((prev) => ({ ...prev, ...formUpdates }));
    setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
    fetchOrders(c.contactId);
    fetchTicketHistory(c.mobile || form.customer_mobile);
  };

  const handleSelectOrder = (order) => {
    setOrdersState((prev) => ({ ...prev, selectedDealId: order.dealId }));
    let autoArea = null;
    if (isAbraaj && order.deliveryAddress) {
      const match = order.deliveryAddress.match(/Area:\s*([^\n\r,]+)/i);
      if (match) {
        const resolved = resolveArea(match[1].trim());
        if (resolved) autoArea = resolved;
      }
    }
    // SAP's order channel is copied into the ticket, but SAP labels
    // call-center orders "CC" while the portal's canonical value is "Phone"
    // (same channel, just SAP's shorthand). Fold it here so new tickets don't
    // reintroduce "CC" into channel_source.
    const normalizeSapChannel = (raw) => (raw === 'CC' ? 'Phone' : raw);
    setForm((prev) => ({
      ...prev,
      sap_order_reference: order.dealId,
      channel_source: prev.channel_source || normalizeSapChannel(order.channel) || prev.channel_source,
      customer_area: (autoArea && !prev.customer_area) ? autoArea : prev.customer_area,
    }));
    if (autoArea && !form.customer_area) setAreaSearch(autoArea);
    setOrderMeta({ custcat1: order.custCat1 || '', custcat2: order.custCat2 || '' });
  };

  const handleSkipOrder = () => {
    setOrdersState((prev) => ({ ...prev, selectedDealId: null }));
    setForm((prev) => ({ ...prev, sap_order_reference: '' }));
    setOrderMeta({ custcat1: '', custcat2: '' });
  };

  const normalizeMobile = (raw) => {
    let d = String(raw).replace(/[^0-9]/g, '');
    if (d.startsWith('965') && d.length > 8) d = d.slice(3);
    return d;
  };

  const triggerLookup = async (normalizedOverride) => {
    const raw = form.customer_mobile.trim();
    if (!raw && !normalizedOverride) return;
    const mobile = normalizedOverride || raw.replace(/^\+?965/, '').trim();
    if (!mobile || mobile.length < 4) return;

    fetchOrdersIdRef.current += 1;
    ticketHistoryIdRef.current += 1;
    setCustomerLookup({ status: 'loading' });
    setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
    setOrderMeta({ custcat1: '', custcat2: '' });
    setTicketHistory({ status: 'idle', tickets: [] });

    try {
      const slug = entitySlugRef.current || '';
      const res = await api.get(`/hubspot/customer?mobile=${encodeURIComponent(mobile)}&entity=${encodeURIComponent(slug)}`);
      const data = res.data;
      if (!data.found || !data.customers?.length) {
        setCustomerLookup({ status: 'not_found' });
        return;
      }
      const customers = data.customers;
      const contactId = customers[0].contactId || '';
      hubspotContactIdRef.current = contactId;
      setForm((prev) => ({
        ...prev,
        customer_name: customers[0].name || prev.customer_name,
        customer_email: customers[0].email || prev.customer_email,
        hubspot_contact_id: contactId,
      }));
      setCustomerLookup({ status: 'found', source: 'HubSpot', customers, selectedIndex: 0 });
      fetchOrders(contactId);
      fetchTicketHistory(mobile);
    } catch {
      setCustomerLookup({ status: 'idle' });
    }
  };

  const handleMobileChange = (raw) => {
    set('customer_mobile', raw);
    setCustomerLookup({ status: 'idle' });
    setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
    setTicketHistory({ status: 'idle', tickets: [] });
    hubspotContactIdRef.current = '';
    setForm((prev) => ({ ...prev, hubspot_contact_id: '' }));
    const normalized = normalizeMobile(raw);
    if (normalized.length === 8 && normalized !== lastLookedUp) {
      setLastLookedUp(normalized);
      triggerLookup(normalized);
    }
  };

  const handleEntitySelect = (btnId) => {
    if (entityLocked) return;
    if (String(btnId) === String(form.entity_id)) return;
    setCustomerLookup({ status: 'idle' });
    setOrdersState({ status: 'idle', orders: [], selectedDealId: null });
    setOrderMeta({ custcat1: '', custcat2: '' });
    setTicketHistory({ status: 'idle', tickets: [] });
    hubspotContactIdRef.current = '';
    setForm((prev) => ({
      ...prev, entity_id: String(btnId),
      complaint_group: '', complaint_type: '', complaint_subtype: '',
      customer_name: '', customer_email: '', sap_order_reference: '', hubspot_contact_id: '',
    }));
  };

  const handleGroupSelect = (group) => {
    setUiGroup(group);
    setForm((prev) => ({ ...prev, complaint_group: '', complaint_type: '', complaint_subtype: '' }));
    setErrors((prev) => ({ ...prev, complaint_group: '', complaint_type: '' }));
  };

  const handleTypeSelect = (type) => { set('complaint_type', type); };

  // Photos setter — enforces the 5-file cap and JPG/PNG/WEBP MIME check.
  // Silently drops rejected files (matches live behaviour) and toasts if any invalid.
  const handleSetPhotos = (nextList) => {
    const arr = Array.isArray(nextList) ? nextList : [];
    const valid = arr.filter((f) => PHOTO_ACCEPT.includes(f.type));
    if (valid.length < arr.length) toast.error('Only JPG, PNG, or WEBP images allowed');
    setPhotos(valid.slice(0, PHOTO_MAX));
  };

  const validate = () => {
    const e = {};
    if (!form.entity_id) e.entity_id = 'Entity required';
    if (!form.subject.trim()) e.subject = 'Subject required';
    if (!form.complaint_group) e.complaint_group = 'Complaint group required';
    if (!form.complaint_type) e.complaint_type = 'Complaint type required';
    if (!form.channel_source) e.channel_source = 'Channel source required';
    if (!form.customer_mobile.trim()) e.customer_mobile = 'Customer mobile required';
    if (form.complaint_group === 'cancel_order' && !form.cancellation_reason) e.cancellation_reason = 'Cancellation reason is required for cancel order tickets.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fix the errors before submitting'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      if (orderMeta.custcat1) formData.append('custcat1', orderMeta.custcat1);
      if (orderMeta.custcat2) formData.append('custcat2', orderMeta.custcat2);
      photos.forEach((p) => formData.append('complaint_photo', p));

      const res = await api.post('/tickets', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Ticket ${res.data.ticket_number} created successfully`);

      // Backend routing engine resolved the department already; escalate call
      // moves the ticket into the dept lifecycle. Duplicate creation
      // notification is suppressed server-side (Sprint 11.2 creation window).
      const routedDept = res.data.escalated_to_dept;
      if (routedDept) {
        try {
          await api.post(`/tickets/${res.data.id}/escalate`, {
            dept_slug: routedDept,
            actor_id: user?.id,
            actor_name: user?.name,
            actor_role: user?.role,
            reason: 'Auto-routed on ticket creation',
          });
        } catch (err) {
          console.error('Auto-escalation failed (non-fatal):', err.message);
        }
      }

      // Re-fetch ticket history so the newly created ticket appears if the user
      // stays on the customer view (rare; navigate below usually wins).
      const mobileForHistory = form.customer_mobile;
      if (mobileForHistory) {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        await fetchTicketHistory(mobileForHistory);
      }

      navigate(`/tickets/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <NewTicketView
      form={form}
      set={set}
      entitySlug={entitySlug}
      isAbraaj={isAbraaj}
      isLofat={isLofat}
      entityLocked={entityLocked}
      loading={loading}
      errors={errors}
      complaintData={complaintData}
      groups={groups}
      types={types}
      uiGroup={uiGroup}
      autoPriority={autoPriority}
      routingHint={routingHint}
      agents={agents}
      customerLookup={customerLookup}
      ordersState={ordersState}
      ticketHistory={ticketHistory}
      areaSearch={areaSearch}
      setAreaSearch={setAreaSearch}
      photos={photos}
      setPhotos={handleSetPhotos}
      user={user}
      onSubmit={handleSubmit}
      onEntitySelect={handleEntitySelect}
      onGroupSelect={handleGroupSelect}
      onTypeSelect={handleTypeSelect}
      onMobileChange={handleMobileChange}
      onMobileEnter={() => triggerLookup()}
      triggerLookup={() => triggerLookup()}
      onSelectCustomer={handleSelectCustomer}
      onSelectOrder={handleSelectOrder}
      onSkipOrder={handleSkipOrder}
      onCancel={() => navigate(-1)}
    />
  );
}
